import { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp, TrendingDown, CheckCircle2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const RetailScore = () => {
  const [formData, setFormData] = useState({
    assetklasse: '',
    hauptmieter_kategorie: '',
    jahresmiete_band: '',
    restlaufzeit_band: '',
    flaeche_band: ''
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCalculate = async () => {
    const { assetklasse, hauptmieter_kategorie, jahresmiete_band, restlaufzeit_band, flaeche_band } = formData;
    
    if (!assetklasse || !hauptmieter_kategorie || !jahresmiete_band || !restlaufzeit_band || !flaeche_band) {
      toast.error('Bitte füllen Sie alle Felder aus');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/tool/retail-score`, formData);
      setResult(response.data);
      toast.success('Score berechnet!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler bei der Berechnung');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score === 'A') return 'var(--success-600)';
    if (score === 'B') return 'var(--gold-600)';
    if (score === 'C') return 'var(--warning-600)';
    return 'var(--error-600)';
  };

  const getScoreBg = (score) => {
    if (score === 'A') return 'var(--success-100)';
    if (score === 'B') return 'var(--gold-100)';
    if (score === 'C') return 'var(--warning-100)';
    return 'var(--error-100)';
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Retail Score Light - Handelsimmobilie bewerten"
        description="Schnelle Ersteinschätzung Ihrer Handelsimmobilie mit Score A-D. Top Treiber, Risiken und konkrete Empfehlungen in 2 Minuten."
        url="/tools/retail-score"
      />
      <Navbar />
      <main className="flex-grow bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="w-20 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-6 mx-auto"></div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
              Retail Score Light
            </h1>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--gray-600)' }}>
              Schnelle Ersteinschätzung der Verkaufsfähigkeit Ihrer Handelsimmobilie
            </p>
          </div>

          <Card className="glass-card p-8 mb-8" data-testid="retail-score-form">
            <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>
              Objektdaten
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="assetklasse">Assetklasse *</Label>
                <Select value={formData.assetklasse} onValueChange={(v) => setFormData({...formData, assetklasse: v})}>
                  <SelectTrigger id="assetklasse">
                    <SelectValue placeholder="Bitte wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Lebensmittelmarkt">Lebensmittelmarkt</SelectItem>
                    <SelectItem value="Discounter">Discounter</SelectItem>
                    <SelectItem value="Drogerie">Drogerie</SelectItem>
                    <SelectItem value="Baumarkt">Baumarkt</SelectItem>
                    <SelectItem value="Fachmarkt">Fachmarkt</SelectItem>
                    <SelectItem value="Fachmarktzentrum">Fachmarktzentrum</SelectItem>
                    <SelectItem value="Großfläche">Großfläche</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="mieter">Hauptmieter-Kategorie *</Label>
                <Select value={formData.hauptmieter_kategorie} onValueChange={(v) => setFormData({...formData, hauptmieter_kategorie: v})}>
                  <SelectTrigger id="mieter">
                    <SelectValue placeholder="Bitte wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Premium">Premium (Aldi, Lidl, Edeka, Rewe, Obi)</SelectItem>
                    <SelectItem value="Etabliert">Etabliert (bekannte Kette)</SelectItem>
                    <SelectItem value="Regional">Regional (lokale Kette)</SelectItem>
                    <SelectItem value="Unbekannt">Unbekannt / Kleinbetrieb</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="miete">Jahresmiete *</Label>
                <Select value={formData.jahresmiete_band} onValueChange={(v) => setFormData({...formData, jahresmiete_band: v})}>
                  <SelectTrigger id="miete">
                    <SelectValue placeholder="Bitte wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unter_50k">Unter 50.000 €</SelectItem>
                    <SelectItem value="50k_100k">50.000 - 100.000 €</SelectItem>
                    <SelectItem value="100k_200k">100.000 - 200.000 €</SelectItem>
                    <SelectItem value="200k_500k">200.000 - 500.000 €</SelectItem>
                    <SelectItem value="ueber_500k">Über 500.000 €</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="laufzeit">Restlaufzeit Mietvertrag *</Label>
                <Select value={formData.restlaufzeit_band} onValueChange={(v) => setFormData({...formData, restlaufzeit_band: v})}>
                  <SelectTrigger id="laufzeit">
                    <SelectValue placeholder="Bitte wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unter_3">Unter 3 Jahre</SelectItem>
                    <SelectItem value="3_5">3-5 Jahre</SelectItem>
                    <SelectItem value="5_8">5-8 Jahre</SelectItem>
                    <SelectItem value="8_12">8-12 Jahre</SelectItem>
                    <SelectItem value="ueber_12">Über 12 Jahre</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="flaeche">Verkaufsfläche *</Label>
                <Select value={formData.flaeche_band} onValueChange={(v) => setFormData({...formData, flaeche_band: v})}>
                  <SelectTrigger id="flaeche">
                    <SelectValue placeholder="Bitte wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unter_500">Unter 500 m²</SelectItem>
                    <SelectItem value="500_1500">500 - 1.500 m²</SelectItem>
                    <SelectItem value="1500_3000">1.500 - 3.000 m²</SelectItem>
                    <SelectItem value="3000_8000">3.000 - 8.000 m²</SelectItem>
                    <SelectItem value="ueber_8000">Über 8.000 m²</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              onClick={handleCalculate}
              disabled={loading}
              data-testid="calculate-score-button"
              className="w-full mt-8 btn-gold-gradient text-white border-0 font-semibold py-6 text-lg"
            >
              {loading ? 'Berechne...' : 'Score berechnen'}
            </Button>
          </Card>

          {result && (
            <div className="space-y-6">
              <Card
                className="glass-card p-10 text-center"
                style={{ borderTop: `6px solid ${getScoreColor(result.score)}` }}
                data-testid="score-result"
              >
                <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                  Ihr Retail Score
                </h2>
                <div
                  className="w-32 h-32 rounded-full mx-auto mb-6 flex items-center justify-center shadow-2xl"
                  style={{ backgroundColor: getScoreBg(result.score) }}
                >
                  <span
                    className="text-6xl font-bold"
                    style={{ color: getScoreColor(result.score) }}
                  >
                    {result.score}
                  </span>
                </div>
                <p className="text-lg" style={{ color: 'var(--gray-700)' }}>
                  Punkte: {result.punkte}/100
                </p>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="glass-card p-8">
                  <h3
                    className="text-xl font-bold mb-4 flex items-center gap-2"
                    style={{ color: 'var(--success-600)' }}
                  >
                    <TrendingUp className="h-6 w-6" />
                    Top Treiber
                  </h3>
                  <ul className="space-y-3">
                    {result.top_treiber.map((t, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm" style={{ color: 'var(--gray-700)' }}>
                        <CheckCircle2
                          className="h-5 w-5 mt-0.5 flex-shrink-0"
                          style={{ color: 'var(--success-600)' }}
                        />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </Card>

                <Card className="glass-card p-8">
                  <h3
                    className="text-xl font-bold mb-4 flex items-center gap-2"
                    style={{ color: 'var(--error-600)' }}
                  >
                    <TrendingDown className="h-6 w-6" />
                    Top Risiken
                  </h3>
                  <ul className="space-y-3">
                    {result.top_risiken.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm" style={{ color: 'var(--gray-700)' }}>
                        <span className="text-red-500 font-bold mt-0.5">!</span>
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </div>

              <Card className="glass-card p-8">
                <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--navy-800)' }}>
                  Empfehlungen
                </h3>
                <ul className="space-y-3">
                  {result.empfehlungen.map((emp, i) => (
                    <li key={i} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                      <span className="font-bold" style={{ color: 'var(--gold-600)' }}>
                        {i + 1}.
                      </span>
                      <span style={{ color: 'var(--gray-700)' }}>{emp}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/ersteinschaetzung" className="flex-1">
                  <Button className="w-full btn-gold-gradient text-white border-0 font-semibold py-6">
                    Detaillierte Analyse anfragen
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/kontakt" className="flex-1">
                  <Button
                    variant="outline"
                    className="w-full font-semibold py-6"
                    style={{ borderColor: 'var(--navy-600)', color: 'var(--navy-600)' }}
                  >
                    Checkliste herunterladen
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default RetailScore;
